// components
import { Button, Typography } from '@mui/material';
import { Icon } from '@sdb/planning-ui';
import {
  StyledBoxFlexColumn,
  StyledBoxFlexColumnSmallGap
} from 'components/shared/Box/Box.styles';
import ControlledDutyCodeSelectorBase from 'components/shared/ControlledFields/ControlledDutyCodeSelectorBase';
import ControlledFunctionSelector from 'components/shared/ControlledFields/ControlledFunctionSelector';
import ControlledQualificationSelector from 'components/shared/ControlledFields/ControlledQualificationSelector';
import { Dialog } from 'components/shared';
import {
  StyledCustomWarningContainer,
  StyledDialogActionsMarginTop
} from './UpdateMatchInfoDialog.styles';

// hooks
import { useCallback, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import useAddOrRemoveQualification from '../hooks/useAddOrRemoveQualification';
import useUpdateFlexMatchRequestDutyCodeAndFunction from '../hooks/useUpdateFlexMatchRequestDutyCodeAndFunction';

// types
import { MatchDrawerInfoUpdateEnum } from './MatchDrawerInfoContent.types';

// utils
import dayjs from 'dayjs';
import {
  FormDefaultValues,
  UpdateMatchInfoDialogProps
} from './UpdateMatchInfoDialog.types';

export const UpdateMatchInfoDialog = ({
  isOpen,
  onClose,
  selectedRow,
  updateType,
  handleMatchDrawerClose,
  requestQualifications
}: UpdateMatchInfoDialogProps) => {
  if (!selectedRow) return null;

  const { t } = useTranslation('common', {
    keyPrefix: 'flexMatch'
  });
  const {
    mutateAsync: updateFlexMatchRequestDutyCodeAndFunction,
    isLoading: isDutyCodeAndFunctionLoading
  } = useUpdateFlexMatchRequestDutyCodeAndFunction({
    isWebSocketUpdate: false
  });

  const initialQualificationIds = (requestQualifications ?? []).map(
    (qualification) => qualification.id
  );

  const {
    mutateAsync: addOrRemoveQualification,
    isLoading: isAddOrRemoveQualificationLoading
  } = useAddOrRemoveQualification({ isWebSocketUpdate: true });

  const defaultValues =
    updateType === MatchDrawerInfoUpdateEnum.DutyCodeAndFunction
      ? { dutyId: selectedRow.dutyId, functionId: selectedRow.functionId }
      : { qualifications: initialQualificationIds };

  const methods = useForm<FormDefaultValues>({
    defaultValues
  });

  const { control, handleSubmit } = methods;

  const handleClose = () => {
    onClose?.();
  };

  const title =
    updateType === MatchDrawerInfoUpdateEnum.DutyCodeAndFunction
      ? t('updateDutyCodeOrFunction')
      : t('addOrUpdateQualifications');

  const notificationText =
    updateType === MatchDrawerInfoUpdateEnum.DutyCodeAndFunction &&
    t('updateDutyCodeOrFunctionWarning');

  const onSubmit = (data) => {
    updateType === MatchDrawerInfoUpdateEnum.DutyCodeAndFunction &&
      updateFlexMatchRequestDutyCodeAndFunction(
        {
          requestId: selectedRow.requestId,
          body: {
            dutyId: data.dutyId?.dutyCodeId ?? selectedRow.dutyId,
            functionId: data.functionId ?? selectedRow.functionId
          }
        },
        {
          onSuccess: () => {
            handleClose();
            handleMatchDrawerClose?.();
          }
        }
      );
  };

  const handleQualificationChange = useCallback(
    async ({ addedIds, removedIds }) => {
      for (const id of addedIds) {
        await addOrRemoveQualification({
          type: 'add',
          body: { qualificationId: id, requestId: selectedRow.requestId }
        });
      }
      for (const id of removedIds) {
        await addOrRemoveQualification({
          type: 'remove',
          body: { qualificationId: id, requestId: selectedRow.requestId }
        });
      }
    },
    [addOrRemoveQualification, selectedRow?.requestId]
  );

  useEffect(() => {
    methods.reset(defaultValues);
  }, [updateType, selectedRow?.requestId]);

  return (
    <Dialog
      open={isOpen}
      title={title}
      handleClose={handleClose}
      slotProps={{
        paper: {
          sx: {
            width: '400px'
          }
        }
      }}
    >
      <FormProvider {...methods}>
        <StyledBoxFlexColumn>
          {updateType === MatchDrawerInfoUpdateEnum.DutyCodeAndFunction && (
            <>
              <StyledCustomWarningContainer>
                <Icon type="triangle-exclamation" />
                <Typography variant="body1">{notificationText}</Typography>
              </StyledCustomWarningContainer>

              <StyledBoxFlexColumnSmallGap>
                <ControlledDutyCodeSelectorBase
                  control={control}
                  label={t('dutyCode', { keyPrefix: '' })}
                  name="dutyId"
                  referenceDate={dayjs(selectedRow.date).toDate()}
                  disabled={isDutyCodeAndFunctionLoading}
                  fullWidth
                />

                <ControlledFunctionSelector
                  control={control}
                  label={t('function', { keyPrefix: '' })}
                  name={'functionId'}
                  startDate={dayjs(selectedRow.dutyStartTime)}
                  endDate={dayjs(selectedRow.dutyEndTime)}
                  onlyPreferred
                  onlySelectable
                  fullWidth
                />
              </StyledBoxFlexColumnSmallGap>
            </>
          )}
          {updateType === MatchDrawerInfoUpdateEnum.Qualifications && (
            <ControlledQualificationSelector
              control={control}
              label={t('qualifications', { keyPrefix: '' })}
              name="qualifications"
              placeholder={t('qualifications', { keyPrefix: '' })}
              disabled={isAddOrRemoveQualificationLoading}
              costCenterId={selectedRow.costCenterId}
              onChangeDiff={handleQualificationChange}
              fullWidth
            />
          )}
        </StyledBoxFlexColumn>

        <StyledDialogActionsMarginTop>
          <Button variant="text" color="primary" onClick={handleClose}>
            {t('cancel', { keyPrefix: '' })}
          </Button>
          {updateType === MatchDrawerInfoUpdateEnum.DutyCodeAndFunction && (
            <Button
              variant="contained"
              color="primary"
              disabled={isDutyCodeAndFunctionLoading}
              onClick={handleSubmit(onSubmit)}
            >
              {t('save', { keyPrefix: '' })}
            </Button>
          )}
        </StyledDialogActionsMarginTop>
      </FormProvider>
    </Dialog>
  );
};
